import { useEffect } from 'react'
import { Link } from 'react-router-dom'

const sections = [
  {
    icon: '📜',
    title: 'Acceptance of Terms',
    color: '#4a9eff',
    content: `By accessing or using SysNerve (sysnerve.com), you agree to be bound by these Terms of Service ("Terms") and our Privacy Policy. If you do not agree with any part of these Terms, you may not use our service.

These Terms apply to all visitors, users, and others who access or use SysNerve. By creating an account or using our platform, you represent that you are at least 18 years of age and have the legal capacity to enter into a binding agreement.

SysNerve reserves the right to update these Terms at any time. We will provide reasonable notice of material changes via email or in-app notification. Continued use of the service after changes take effect constitutes acceptance of the revised Terms.

If you are using SysNerve on behalf of a company or organization, you represent that you have authority to bind that entity to these Terms, and that entity accepts these Terms.`,
  },
  {
    icon: '⚙️',
    title: 'Use of Service',
    color: '#4ade80',
    content: `SysNerve grants you a limited, non-exclusive, non-transferable license to access and use our AI Bug Intelligence Platform for your internal business or personal software development purposes.

**Permitted Uses:** You may submit bug reports, stack traces, screenshots, and log files for AI analysis; access your analysis history; share analysis results with your team; and integrate SysNerve with supported third-party tools.

**Account Responsibility:** You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at support@sysnerve.com if you suspect unauthorized access.

**Fair Use:** Automated scraping, API abuse, or using SysNerve in ways that impose excessive load on our infrastructure is prohibited. Plans have stated analysis limits; exceeding them may result in throttling or suspension.

**API Access:** If you access SysNerve via API, you must comply with the API rate limits and usage policies documented in our developer documentation. API keys must not be shared or embedded in client-side code.`,
  },
  {
    icon: '🤖',
    title: 'AI-Generated Content Disclaimer',
    color: '#7c3aed',
    content: `SysNerve uses artificial intelligence to analyze bug reports and generate diagnostic outputs. By using this service, you acknowledge and accept the following:

**No Guarantee of Accuracy:** AI-generated analyses are probabilistic in nature. SysNerve provides confidence scores alongside results, but we cannot guarantee that every analysis is correct. Do not take actions on production systems based solely on SysNerve's output without independent verification.

**Not a Substitute for Professional Judgment:** SysNerve is a productivity tool designed to assist software engineers. It does not replace the judgment of qualified engineers, especially for critical, security-sensitive, or compliance-related bugs.

**Hallucinations and Errors:** Large language models can sometimes produce incorrect or misleading outputs. SysNerve's engineering team continuously works to reduce such occurrences, but cannot eliminate them entirely.

**Use at Your Own Risk:** Any actions taken based on SysNerve's output — including code changes, deployments, or incident responses — are taken at your own risk. SysNerve is not liable for damages arising from reliance on AI-generated content.

**Model Updates:** Our underlying AI models may change over time. This may affect analysis style, output format, or accuracy characteristics. We do not guarantee consistent output formats across model versions.`,
  },
  {
    icon: '🚫',
    title: 'Prohibited Uses',
    color: '#ef4444',
    content: `You may not use SysNerve for any of the following purposes:

**Security Exploits:** Using SysNerve to analyze vulnerabilities with intent to exploit them, or to identify attack surfaces in systems you do not own or have authorization to test.

**Malicious Code:** Submitting malware, ransomware, spyware, or any code designed to harm users or systems for analysis or any other purpose.

**Data Exfiltration:** Attempting to extract other users' data, analysis histories, or any non-public information from SysNerve's systems.

**Impersonation:** Creating accounts under false identities or impersonating other individuals or organizations.

**Reselling:** Reselling, sublicensing, or commercializing access to SysNerve's platform or API without our written permission.

**Circumventing Limits:** Attempting to bypass usage limits, rate limits, or access controls through technical means.

**Spam and Abuse:** Submitting irrelevant or nonsensical content to abuse the AI engine or degrade service quality for others.

Violation of these prohibitions may result in immediate account termination and, where applicable, legal action.`,
  },
  {
    icon: '🔐',
    title: 'Account Termination',
    color: '#f59e0b',
    content: `**Termination by You:** You may cancel your SysNerve account at any time from your account settings. Upon cancellation, your paid subscription will remain active until the end of the current billing period, after which access will be downgraded or terminated.

**Termination by SysNerve:** We reserve the right to suspend or terminate your account at our discretion, with or without notice, if we determine that you have violated these Terms, engaged in fraudulent activity, or pose a risk to the platform or other users.

**Effects of Termination:** Upon termination, your right to access SysNerve ceases immediately. Your data will be retained for 30 days after termination (to allow for reactivation or data export requests) and then permanently deleted, unless legal obligations require longer retention.

**Refunds:** Refund eligibility is governed by our Refund Policy. Generally, we do not offer refunds for partial months of service, except where required by law or at our discretion for exceptional circumstances.

**Survival:** Sections relating to intellectual property, disclaimers, limitation of liability, and governing law survive termination of these Terms.`,
  },
  {
    icon: '⚖️',
    title: 'Limitation of Liability',
    color: '#4a9eff',
    content: `To the maximum extent permitted by applicable law:

**Disclaimer of Warranties:** SysNerve is provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not warrant that the service will be uninterrupted, error-free, or completely secure.

**Limitation of Damages:** In no event shall SysNerve, its directors, employees, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data loss, business interruption, or reputational harm, arising from your use of or inability to use the service.

**Cap on Liability:** Our total liability to you for any claim arising out of or relating to these Terms or the service shall not exceed the greater of (a) the amount you paid us in the 12 months preceding the claim, or (b) USD $100.

**Force Majeure:** SysNerve is not liable for failures or delays caused by events beyond our reasonable control, including internet outages, infrastructure failures, natural disasters, or third-party service interruptions (including AI provider outages).

These limitations apply regardless of the form of action and even if SysNerve has been advised of the possibility of such damages.`,
  },
  {
    icon: '📝',
    title: 'Changes to Terms',
    color: '#4ade80',
    content: `SysNerve reserves the right to modify these Terms of Service at any time. We are committed to transparent communication about significant changes.

**Minor Changes:** Corrections, clarifications, or non-material updates may be made without prior notice. The "last updated" date will always reflect the most recent revision.

**Material Changes:** For changes that materially affect your rights or obligations, we will provide at least 14 days advance notice via email to the address associated with your account, or via a prominent in-app notification.

**Continued Use:** Your continued use of SysNerve after the effective date of any changes constitutes your acceptance of the modified Terms. If you disagree with the changes, you should discontinue use of the service before the changes take effect.

**History:** Previous versions of our Terms are available upon request by contacting support@sysnerve.com.`,
  },
  {
    icon: '🌍',
    title: 'Governing Law',
    color: '#7c3aed',
    content: `These Terms of Service shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law principles.

**Dispute Resolution:** Any disputes arising out of or relating to these Terms or your use of SysNerve shall first be attempted to be resolved through good-faith negotiation. If negotiation fails, disputes shall be submitted to binding arbitration under the rules of the American Arbitration Association (AAA), conducted in English.

**Class Action Waiver:** You agree to resolve disputes with SysNerve on an individual basis only, and not as part of a class, collective, or representative action.

**Jurisdiction:** For matters not subject to arbitration, you consent to the exclusive jurisdiction of courts located in Delaware, USA.

**International Users:** SysNerve is operated from the United States. If you access the service from outside the US, you do so at your own risk and are responsible for compliance with local laws. We make no representation that the service is appropriate or available in all jurisdictions.

**Contact for Legal Matters:** support@sysnerve.com`,
  },
]

function Section({ section, index }) {
  return (
    <div
      style={{
        background: '#05080f',
        border: '1px solid #0f1e35',
        borderRadius: 16,
        marginBottom: 20,
        overflow: 'hidden',
        transition: 'border-color 0.3s',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = section.color + '33'}
      onMouseLeave={e => e.currentTarget.style.borderColor = '#0f1e35'}
    >
      <div style={{
        padding: '20px 28px',
        borderBottom: '1px solid #0f1e35',
        display: 'flex', alignItems: 'center', gap: 14,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: section.color + '12',
          border: `1px solid ${section.color}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, flexShrink: 0,
        }}>
          {section.icon}
        </div>
        <div>
          <div style={{ fontSize: 10, color: section.color, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>
            Article {String(index + 1).padStart(2, '0')}
          </div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#e2e8f0', fontFamily: "'Syne', sans-serif" }}>
            {section.title}
          </h2>
        </div>
      </div>
      <div style={{ padding: '22px 28px' }}>
        {section.content.split('\n\n').map((para, i) => {
          const parts = para.split(/\*\*(.+?)\*\*/g)
          return (
            <p key={i} style={{ fontSize: 14, color: '#64748b', lineHeight: 1.9, margin: '0 0 14px' }}>
              {parts.map((part, j) =>
                j % 2 === 1
                  ? <strong key={j} style={{ color: '#94a3b8', fontWeight: 700 }}>{part}</strong>
                  : part
              )}
            </p>
          )
        })}
      </div>
    </div>
  )
}

export default function Terms() {
  useEffect(() => {
  window.scrollTo(0, 0)
}, [])
  return (
    <div style={{
      minHeight: '100vh',
      background: '#000',
      color: '#e2e8f0',
      fontFamily: "'Segoe UI', sans-serif",
      position: 'relative',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #1e2d45; border-radius: 3px; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        h1, h2, h3 { font-family: 'Syne', sans-serif !important; }
        .back-link { color: #475569; text-decoration: none; font-size: 13px; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1px solid #1e3a5f; border-radius: 8px; transition: all 0.2s; background: #05080f; }
        .back-link:hover { color: #4a9eff; border-color: rgba(74,158,255,0.4); background: rgba(74,158,255,0.05); }
      `}</style>

      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        backgroundImage: 'linear-gradient(#1e3a5f05 1px,transparent 1px),linear-gradient(90deg,#1e3a5f05 1px,transparent 1px)',
        backgroundSize: '56px 56px',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 860, margin: '0 auto', padding: '60px 5% 100px' }}>

        <div style={{ marginBottom: 48, animation: 'fadeUp 0.5s ease' }}>
          <Link to="/" className="back-link">← Back to SysNerve</Link>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 56, animation: 'fadeUp 0.6s ease 0.1s both' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#050810', border: '1px solid #1e3a5f',
            borderRadius: 24, padding: '6px 16px', marginBottom: 24,
          }}>
            <span style={{ fontSize: 20, animation: 'float 3s ease-in-out infinite' }}>⚡</span>
            <span style={{
              fontSize: 18, fontWeight: 900,
              background: 'linear-gradient(135deg,#4a9eff,#7c3aed)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              fontFamily: "'Syne', sans-serif",
            }}>SysNerve</span>
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 900, margin: '0 0 14px', lineHeight: 1.1 }}>
            Terms of <span style={{ color: '#4a9eff' }}>Service</span>
          </h1>
          <p style={{ color: '#475569', fontSize: 14, margin: 0, lineHeight: 1.7 }}>
            Last updated: June 1, 2026 · Effective: June 1, 2026
          </p>
          <div style={{
            marginTop: 20, display: 'inline-block',
            background: 'rgba(74,158,255,0.06)', border: '1px solid rgba(74,158,255,0.2)',
            borderRadius: 10, padding: '10px 20px',
          }}>
            <span style={{ fontSize: 13, color: '#4a9eff', fontWeight: 600 }}>
              📋 Please read these terms carefully before using SysNerve.
            </span>
          </div>
        </div>

        {/* Intro */}
        <div style={{
          background: '#05080f', border: '1px solid #1e3a5f',
          borderRadius: 14, padding: '20px 24px', marginBottom: 24,
          animation: 'fadeUp 0.65s ease 0.15s both',
        }}>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.9 }}>
            These Terms of Service govern your use of the SysNerve AI Bug Intelligence Platform, operated by <strong style={{ color: '#94a3b8' }}>SysNerve Inc.</strong> ("SysNerve", "we", "us", or "our"). These Terms constitute a legally binding agreement between you and SysNerve. References to "you" or "your" mean any individual or entity accessing or using our service.
          </p>
        </div>

        {/* Sections */}
        <div style={{ animation: 'fadeUp 0.7s ease 0.2s both' }}>
          {sections.map((section, i) => (
            <Section key={i} section={section} index={i} />
          ))}
        </div>

        {/* Footer note */}
        <div style={{
          marginTop: 40, textAlign: 'center',
          borderTop: '1px solid #0f1e35', paddingTop: 32,
          animation: 'fadeUp 0.8s ease 0.3s both',
        }}>
          <p style={{ fontSize: 13, color: '#334155', margin: '0 0 16px', lineHeight: 1.7 }}>
            Legal questions? Contact us at{' '}
            <a href="mailto:support@sysnerve.com" style={{ color: '#4a9eff', textDecoration: 'none' }}>support@sysnerve.com</a>
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/" style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>Home</Link>
            <span style={{ color: '#1e293b' }}>·</span>
            <Link to="/faq" style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>FAQ</Link>
            <span style={{ color: '#1e293b' }}>·</span>
            <Link to="/privacy" style={{ color: '#475569', textDecoration: 'none', fontSize: 13 }}>Privacy Policy</Link>
        
          </div>
        </div>
      </div>
    </div>
  )
}

